import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { colors } from 'utils/colors';

const isFontAwesomeIcon = (icon) => {
  if (typeof icon === 'string') {
    return icon.startsWith('fa-') || icon.startsWith('fa ');
  }
  return typeof icon === 'object';
};

function ButtonFilled({ className, children, ...rest }) {
  const isFA = isFontAwesomeIcon(rest.icon);
  return (
    <StyledButton className={className} {...rest}>
      {rest.icon && !isFA && <Icon className={className} name={rest.icon} />}
      {rest.icon && isFA && (
        <i className={cn('icon', 'icon--fa', className)}>
          <FontAwesomeIcon icon={rest.icon} />
        </i>
      )}
      {children}
    </StyledButton>
  );
}

ButtonFilled.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.array,
  ]),
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  floated: PropTypes.string,
  color: PropTypes.string,
  className: PropTypes.string,
};

ButtonFilled.defaultProps = {
  children: '',
  icon: '',
  floated: '',
  color: '',
  className: '',
};

export default ButtonFilled;

const StyledButton = styled(
  ({ margin, color, floated, className, children, icon, ...rest }) =>
    rest.to ? (
      <Link className={className} {...rest}>
        {children}
      </Link>
    ) : (
      // eslint-disable-next-line react/button-has-type
      <button className={className} {...rest}>
        {children}
      </button>
    )
)`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  position: relative;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  min-height: 1em;
  min-width: 95px;
  height: 40px;
  border: none;
  text-align: center;
  outline: 0;
  overflow: hidden;
  color: ${(props) => determineIconColor(props.color)};
  background-color: ${(props) => props.color};
  padding: ${(props) => {
    if (props.nopadding) return '0';

    if (props.icon) {
      return props.className && props.className.includes('left')
        ? '0 1.2em 0 3em'
        : props.className && props.className.includes('right')
        ? '0 3em 0 1.2em'
        : '0 1.2em';
    }

    return '0 1.2em';
  }};
  vertical-align: top;
  float: ${(props) => props.floated || 'initial'};
  margin: ${(props) => props.margin || '0'};
  line-height: 1.4em !important;
  user-select: none !important;

  &:hover {
    opacity: 0.8;
    color: ${(props) => determineIconColor(props.color)};
  }

  &:focus {
  }

  // &:active {
  //   background-color: #f8f8f8;

  //   .icon {
  //     background-color: #f8f8f8;
  //   }
  // }

  &.button-loading {
    color: transparent !important;

    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      margin: -0.6em 0 0 -0.6em;
      width: 16px;
      height: 16px;
      border-radius: 500rem;
      border: 0.2em solid rgba(0, 0, 0, 0.15);
      animation: spin 0.6s linear infinite;
      border-top-color: #767676;
    }

    .icon {
      display: none;
    }
  }

  &.loading {
    cursor: default;
    color: transparent !important;
    pointer-events: none !important;
    transition: none !important;
    &:after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      margin: -0.6em 0 0 -0.6em;
      width: 16px;
      height: 16px;
      border-radius: 500rem;
      border: 0.2em solid rgba(0, 0, 0, 0.15);
      animation: spin 0.6s linear infinite;
      border-top-color: #767676;
    }
    &:focus,
    &:active {
      color: transparent !important;
    }
    .icon {
      display: none;
    }
    @keyframes spin {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }
  }

  &.disabled {
    cursor: default;
    pointer-events: none !important;
    opacity: 0.45;
  }

  &.basic {
    font-size: 1rem;
    padding: 0.78571429em 1.5em;
    line-height: 1rem !important;

    &:hover,
    &:focus {
      color: ${(props) => determineIconColor(props.color)};
      opacity: 0.8;
    }
  }

  &.inverted {
    color: #fff;
    font-size: 1rem;
    line-height: 1.3rem !important;

    &:hover,
    &:focus {
      opacity: 0.8;
      color: ${(props) => determineIconColor(props.color)};
    }
  }

  &.negative {
    && {
      color: #db2828 !important;
    }
  }

  &.left {
    &:focus {
    }

    &:hover {
      opacity: 0.8;
      color: ${(props) => determineIconColor(props.color)};
    }
  }

  &.grey-focus {
    &:focus {
      color: #fff;
    }
  }

  &.grey-focus2 {
    &:focus {
    }
  }

  &.right {
  }

  &.centered {
    display: block;
    margin: 1rem auto 0 auto;
  }

  &.fluid {
    width: 100%;
  }

  .icon {
    margin: 0;
    height: 100%;
    position: absolute;
    color: ${(props) => determineIconColor(props.color)};
    top: 0;
    width: 36px;
    background-color: ${(props) => props.color};
    // transition: all 0.3s ease;

    &.icon--fa {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &:not(.icon--fa):before {
      position: absolute;
      line-height: 1;
      font-size: 1em;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }

    &.left {
      left: 0;
      right: initial;
    }

    &.right {
      right: 0;
      left: initial;
    }

    &.edit:before {
      top: 53%;
      left: 55%;
    }

    &.check:before {
      left: 53%;
    }

    img {
      height: 17px;
      width: 17px;
      transform: rotate(43deg);
      margin: 10px;
    }
  }
`;

const determineIconColor = (color) => {
  switch (color) {
    case colors.darkBlue:
    case colors.red:
    case colors.blue:
    case colors.darkGrey:
    case colors.green:
    case colors.purple:
    case colors.orange:
    case colors.pink:
    case colors.navyBlue:
    case colors.darkTeal:
    case colors.buttonGrey:
    case colors.periwinkle:
      return 'white';
    default:
      return 'rgba(0,0,0,0.6)';
  }
};
